import * as THREE from 'three';
import type { World } from '@/sim/world';
import { KIND_HEAD, KIND_BODY, KIND_ENEMY, KIND_ANCHOR } from '@/sim/world';

const UP = 34;
const TRAIL = 40;
const BURSTS = 24;
const SCUFFS = 160;

export interface Trail {
  push(x: number, y: number): void;
}

export class View {
  readonly gl: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.OrthographicCamera;

  private readonly pucks: THREE.Mesh[] = [];
  private readonly geo: THREE.CylinderGeometry;
  private readonly headGeo: THREE.CylinderGeometry;
  private readonly anchorGeo: THREE.CylinderGeometry;
  private readonly haft: THREE.Line;
  private readonly haftPos: Float32Array;
  private readonly trailPts: Float32Array;
  private readonly trail: THREE.Line;
  private readonly history: number[] = [];
  private readonly bursts: { mesh: THREE.Mesh; life: number; span: number; scale: number }[] = [];
  private readonly scuff: THREE.InstancedMesh;
  private readonly scuffLife = new Float32Array(SCUFFS);
  private readonly scuffMax = new Float32Array(SCUFFS);
  private scuffNext = 0;
  private readonly tmp = new THREE.Object3D();
  private readonly mats: Record<number, THREE.MeshStandardMaterial>;
  private span = 19;
  private readonly torch: THREE.PointLight;
  private boundsX = 0;
  private boundsY = 0;
  private viewX = 0;
  private viewY = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.gl = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
    this.gl.outputColorSpace = THREE.SRGBColorSpace;
    this.gl.toneMapping = THREE.ACESFilmicToneMapping;
    this.gl.toneMappingExposure = 1.05;
    this.gl.shadowMap.enabled = true;
    this.gl.shadowMap.type = THREE.PCFSoftShadowMap;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x07060a);
    this.scene.fog = new THREE.Fog(0x07060a, 22, 60);

    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 200);
    this.camera.position.set(0, UP, 0);
    this.camera.rotation.set(-Math.PI / 2, 0, 0);

    this.torch = new THREE.PointLight(0xffb457, 1500, 34, 2);
    this.torch.position.set(0, 11, 0);
    this.torch.castShadow = true;
    this.torch.shadow.mapSize.set(1024, 1024);
    this.torch.shadow.camera.near = 0.4;
    this.torch.shadow.camera.far = 34;
    this.torch.shadow.bias = -0.004;
    this.scene.add(this.torch);

    const rim = new THREE.DirectionalLight(0x39507a, 0.7);
    rim.position.set(-8, 9, -7);
    this.scene.add(rim);
    this.scene.add(new THREE.AmbientLight(0x2e211a, 0.55));

    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(120, 120),
      new THREE.MeshStandardMaterial({ color: 0x6b5b45, roughness: 0.97, metalness: 0 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    this.scene.add(floor);

    this.geo = new THREE.CylinderGeometry(1, 1, 0.5, 20);
    this.headGeo = new THREE.CylinderGeometry(1, 1, 1.15, 10);
    this.anchorGeo = new THREE.CylinderGeometry(0.86, 1, 3.6, 7);
    this.mats = {
      [KIND_HEAD]: new THREE.MeshStandardMaterial({ color: 0x9fa4ad, roughness: 0.31, metalness: 0.95 }),
      [KIND_BODY]: new THREE.MeshStandardMaterial({ color: 0x8a6438, roughness: 0.66, metalness: 0.22 }),
      [KIND_ENEMY]: new THREE.MeshStandardMaterial({ color: 0x5e2a2c, roughness: 0.86, metalness: 0.04 }),
      [KIND_ANCHOR]: new THREE.MeshStandardMaterial({
        color: 0xc9702a,
        emissive: 0xff7418,
        emissiveIntensity: 1.5,
        roughness: 0.52,
        metalness: 0.4
      }),
      0: new THREE.MeshStandardMaterial({ color: 0x4a4038, roughness: 0.6, metalness: 0.5 })
    };

    this.haftPos = new Float32Array(64 * 3);
    const haftGeo = new THREE.BufferGeometry();
    haftGeo.setAttribute('position', new THREE.BufferAttribute(this.haftPos, 3));
    this.haft = new THREE.Line(
      haftGeo,
      new THREE.LineBasicMaterial({ color: 0xb9a273 })
    );
    this.haft.frustumCulled = false;
    this.scene.add(this.haft);

    const scuffGeo = new THREE.PlaneGeometry(1, 1);
    scuffGeo.rotateX(-Math.PI / 2);
    this.scuff = new THREE.InstancedMesh(
      scuffGeo,
      new THREE.MeshBasicMaterial({
        color: 0x120c08,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
        toneMapped: false
      }),
      SCUFFS
    );
    this.scuff.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.scuff.count = SCUFFS;
    this.scuff.frustumCulled = false;
    for (let i = 0; i < SCUFFS; i += 1) {
      this.tmp.position.set(0, -50, 0);
      this.tmp.scale.set(0.001, 1, 0.001);
      this.tmp.updateMatrix();
      this.scuff.setMatrixAt(i, this.tmp.matrix);
    }
    this.scene.add(this.scuff);

    this.trailPts = new Float32Array(TRAIL * 3);
    const trailGeo = new THREE.BufferGeometry();
    trailGeo.setAttribute('position', new THREE.BufferAttribute(this.trailPts, 3));
    this.trail = new THREE.Line(
      trailGeo,
      new THREE.LineBasicMaterial({ color: 0xffd9a0, transparent: true, opacity: 0.7 })
    );
    this.trail.frustumCulled = false;
    this.scene.add(this.trail);

    const ringGeo = new THREE.RingGeometry(0.82, 1, 32);
    ringGeo.rotateX(-Math.PI / 2);
    for (let i = 0; i < BURSTS; i += 1) {
      const mat = new THREE.MeshBasicMaterial({
        color: 0xffe6b0,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        toneMapped: false
      });
      const ring = new THREE.Mesh(ringGeo, mat);
      ring.visible = false;
      ring.rotation.x = 0;
      this.scene.add(ring);
      this.bursts.push({ mesh: ring, life: 0, span: 1, scale: 1 });
    }
  }

  setBounds(halfX: number, halfY: number): void {
    this.boundsX = halfX;
    this.boundsY = halfY;
  }

  resize(w: number, h: number, dpr: number): void {
    this.gl.setPixelRatio(Math.min(dpr, 2));
    this.gl.setSize(w, h, false);
    const aspect = w / Math.max(1, h);
    const half = this.span;
    this.camera.left = -half * aspect;
    this.camera.right = half * aspect;
    this.camera.top = half;
    this.camera.bottom = -half;
    this.camera.updateProjectionMatrix();
    this.viewX = half * aspect;
    this.viewY = half;
  }

  screenToWorld(cx: number, cy: number, w: number, h: number): [number, number] {
    const aspect = w / Math.max(1, h);
    const nx = (cx / w) * 2 - 1;
    const ny = (cy / h) * 2 - 1;
    return [this.camera.position.x + nx * this.span * aspect, this.camera.position.z + ny * this.span];
  }

  syncWalls(world: World): void {
    const mat = new THREE.MeshStandardMaterial({ color: 0x3c3128, roughness: 0.92, metalness: 0.03 });
    for (const w of world.walls) {
      const box = new THREE.Mesh(
        new THREE.BoxGeometry(w.maxX - w.minX, 2.2, w.maxY - w.minY),
        mat
      );
      box.position.set((w.minX + w.maxX) / 2, 1.1, (w.minY + w.maxY) / 2);
      box.castShadow = true;
      box.receiveShadow = true;
      this.scene.add(box);
    }
  }

  sync(world: World, headIndex: number, chain: number[]): void {
    const bodies = world.bodies;
    while (this.pucks.length < bodies.length) {
      const m = new THREE.Mesh(this.geo, this.mats[0]);
      m.castShadow = true;
      m.receiveShadow = true;
      this.scene.add(m);
      this.pucks.push(m);
    }
    for (let i = 0; i < this.pucks.length; i += 1) {
      const puck = this.pucks[i];
      const b = bodies[i];
      if (!b || !b.alive) {
        puck.visible = false;
        continue;
      }
      puck.visible = true;
      const isHead = b.kind === KIND_HEAD;
      const isAnchor = b.kind === KIND_ANCHOR;
      const want = isHead ? this.headGeo : isAnchor ? this.anchorGeo : this.geo;
      if (puck.geometry !== want) puck.geometry = want;
      puck.material = this.mats[b.kind] ?? this.mats[0];
      puck.scale.set(b.radius, 1, b.radius);
      puck.position.set(
        b.x,
        isAnchor ? b.radius * 1.8 : isHead ? b.radius * 0.6 + 0.2 : b.radius * 0.5 + 0.06,
        b.y
      );
      if (isHead) puck.rotation.y = Math.atan2(b.vy, b.vx);
    }

    const pos = this.haftPos;
    let n = 0;
    for (const idx of chain) {
      const b = bodies[idx];
      pos[n * 3] = b.x;
      pos[n * 3 + 1] = 0.5;
      pos[n * 3 + 2] = b.y;
      n += 1;
    }
    this.haft.geometry.setDrawRange(0, n);
    this.haft.geometry.attributes.position.needsUpdate = true;

    const head = bodies[headIndex];
    this.history.push(head.x, head.radius * 0.5 + 0.12, head.y);
    while (this.history.length > TRAIL * 3) this.history.splice(0, 3);
    const pts = this.trailPts;
    const count = this.history.length / 3;
    for (let i = 0; i < count; i += 1) {
      pts[i * 3] = this.history[i * 3];
      pts[i * 3 + 1] = this.history[i * 3 + 1];
      pts[i * 3 + 2] = this.history[i * 3 + 2];
    }
    this.trail.geometry.attributes.position.needsUpdate = true;
    this.trail.geometry.setDrawRange(0, count);
  }

  burst(x: number, y: number, power: number, killed: boolean): void {
    let slot = this.bursts.find((b) => b.life <= 0);
    if (!slot) slot = this.bursts.reduce((a, b) => (b.life < a.life ? b : a));
    const span = killed ? 0.26 : 0.12 + power * 0.06;
    slot.life = span;
    slot.span = span;
    slot.scale = (killed ? 1.5 : 0.7) + power * 1.3;
    slot.mesh.position.set(x, 0.08, y);
    slot.mesh.visible = true;
    const mat = slot.mesh.material as THREE.MeshBasicMaterial;
    mat.color.setHex(killed ? 0xffb46a : 0xffe6b0);
  }

  mark(x: number, y: number, power: number, angle: number): void {
    if (power < 0.16) return;
    const i = this.scuffNext;
    this.scuffNext = (this.scuffNext + 1) % SCUFFS;
    const life = 2.6 + power * 5.5;
    this.scuffLife[i] = life;
    this.scuffMax[i] = life;
    this.tmp.position.set(x, 0.015, y);
    this.tmp.rotation.set(0, -angle, 0);
    this.tmp.scale.set(0.5 + power * 1.5, 1, 0.1 + power * 0.24);
    this.tmp.updateMatrix();
    this.scuff.setMatrixAt(i, this.tmp.matrix);
    this.scuff.instanceMatrix.needsUpdate = true;
  }

  private stepScuff(dt: number): void {
    let dirty = false;
    for (let i = 0; i < SCUFFS; i += 1) {
      if (this.scuffLife[i] <= 0) continue;
      this.scuffLife[i] -= dt;
      if (this.scuffLife[i] > 0) continue;
      this.tmp.position.set(0, -50, 0);
      this.tmp.scale.set(0.001, 1, 0.001);
      this.tmp.rotation.set(0, 0, 0);
      this.tmp.updateMatrix();
      this.scuff.setMatrixAt(i, this.tmp.matrix);
      dirty = true;
    }
    if (dirty) this.scuff.instanceMatrix.needsUpdate = true;
  }

  private stepBursts(dt: number): void {
    for (let i = 0; i < this.bursts.length; i += 1) {
      const b = this.bursts[i];
      if (b.life <= 0) continue;
      b.life -= dt;
      const mat = b.mesh.material as THREE.MeshBasicMaterial;
      if (b.life <= 0) {
        b.mesh.visible = false;
        mat.opacity = 0;
        continue;
      }
      const t = 1 - b.life / b.span;
      const eased = 1 - (1 - t) * (1 - t);
      const grow = 0.3 + eased * b.scale;
      b.mesh.scale.set(grow, 1, grow);
      mat.opacity = (1 - t) * (1 - t) * 3.2;
    }
  }

  render(focusX: number, focusY: number, roll = 0, dt = 1 / 60): void {
    this.stepBursts(dt);
    this.stepScuff(dt);
    const limitX = Math.max(0, this.boundsX - this.viewX);
    const limitY = Math.max(0, this.boundsY - this.viewY);
    const wantX = Math.max(-limitX, Math.min(limitX, focusX));
    const wantY = Math.max(-limitY, Math.min(limitY, focusY));
    this.camera.position.x += (wantX - this.camera.position.x) * 0.09;
    this.camera.position.z += (wantY - this.camera.position.z) * 0.09;
    this.camera.rotation.z = roll;
    this.torch.position.set(focusX, 11, focusY);
    this.gl.render(this.scene, this.camera);
  }
}
